<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { VisuallyHidden } from 'radix-vue';
import { ColumnDef, SortingState } from '@tanstack/vue-table';
import { parseISO } from 'date-fns';
import { Trash2 } from 'lucide-vue-next';
import { cloneDeep } from 'lodash-es';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { i18n } from '@/plugins/internationalization/i18n';
import { useStudyPeriodSetups } from '@/plugins/store/actions/queries/study-periods';
import { StudyPeriodCategory, StudyPeriodSetup } from '@/types';
import { DataTable } from '@/components/ui/data-table';
import SearchInput from '@/components/ui/input/SearchInput.vue';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import DeleteSavedAnalysisDialog from './DeleteSavedAnalysisDialog.vue';

interface Props {
    categories: StudyPeriodCategory[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
    generate: [setup: StudyPeriodSetup];
}>();

const isOpen = defineModel<boolean>('open', { required: true });

const { data: studyPeriodSetups, isLoading } = useStudyPeriodSetups();

const page = ref(1);
const pageSize = ref(9999);
const pageSizeOptions = ref([10, 25, 50, 100]);

interface RowItem extends StudyPeriodSetup {
    _displayCategories: string;
    _displayTeams: string;
    _displayFavoritTeam: string;
    _displayGrowthReference: string;
}

const columns = computed<ColumnDef<RowItem>[]>(() => [
    {
        header: '',
        accessorKey: '_selection',
        meta: {
            cellClass: 'w-10',
        },
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.name'),
        accessorKey: 'name',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.categories'),
        accessorKey: '_displayCategories',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.teams'),
        accessorKey: '_displayTeams',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.favoriteTeam'),
        accessorKey: '_displayFavoritTeam',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.growthReference'),
        accessorKey: '_displayGrowthReference',
        enableSorting: true,
        enableGlobalFilter: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.animalGroup'),
        accessorKey: '_displayAnimalGroup',
        enableSorting: true,
    },
    {
        header: i18n.global.t('views.monitoring.teamComparison.dialogs.savedAnalyses.table.headers.creationDate'),
        accessorKey: 'created_at_utc',
        enableSorting: true,
    },
    {
        header: '',
        accessorKey: '_actions',
        meta: {
            cellClass: 'w-10',
        },
    },
]);

function getDisplayAnimalGroup(setup: StudyPeriodSetup) {
    switch (setup.animal_group_id) {
        case 1:
            return i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.weaners');
        case 2:
            return i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.slaughterPigs');
        case 3:
            return i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.frats');
        default:
            return '-';
    }
}

const items = computed(() =>
    studyPeriodSetups.value?.map((setup) => ({
        ...setup,
        _displayCategories: props.categories
            .filter((category) => category.options.some((option) => setup.categories.includes(option.id)))
            .flatMap((category) => category.options.filter((option) => setup.categories.includes(option.id)).map((option) => option.name))
            .join(', '),
        _displayTeams: setup.study_periods.map((period) => period.name).join(', '),
        _displayFavoritTeam: setup.favorites.map((period) => period.name).join(', ') || '-',
        _displayGrowthReference: setup.growth_reference?.name || '-',
        _displayAnimalGroup: getDisplayAnimalGroup(setup),
    }))
);

const filter = ref('');
const sorting = ref<SortingState>([
    {
        id: 'created_at_utc',
        desc: true,
    },
]);

const selectedSetup = ref<number | null>(null);

watch(
    studyPeriodSetups,
    async (newVal) => {
        if (newVal) {
            if (selectedSetup.value && newVal.some((setup) => setup.id === selectedSetup.value) && isOpen.value) {
                return;
            }

            const sortedSetups = cloneDeep(newVal).sort((a, b) => new Date(b.created_at_utc).getTime() - new Date(a.created_at_utc).getTime());

            selectedSetup.value = sortedSetups[0]?.id || null;
        }
    },
    { immediate: true }
);

function handleSelectionChange(id: number) {
    selectedSetup.value = id;
}

const studyPeriodSetupToDelete = ref<StudyPeriodSetup | null>(null);
const isDeleteDialogOpen = ref(false);

async function handleDelete(studyPeriodSetup: StudyPeriodSetup) {
    studyPeriodSetupToDelete.value = studyPeriodSetup;

    await nextTick();

    isDeleteDialogOpen.value = true;
}

function handleGenerate() {
    const selectedSetupToGenerate = items.value?.find((item) => item.id === selectedSetup.value);

    if (selectedSetupToGenerate) {
        emit('generate', selectedSetupToGenerate);
    }

    isOpen.value = false;
}
</script>

<template>
    <Dialog v-model="isOpen" class="sm:max-w-screen-xl" @open-auto-focus.prevent>
        <DialogHeader>
            <DialogTitle>
                {{ $t('views.monitoring.teamComparison.dialogs.savedAnalyses.title') }}
            </DialogTitle>
            <VisuallyHidden>
                <DialogDescription>
                    {{ $t('views.monitoring.teamComparison.dialogs.savedAnalyses.description') }}
                </DialogDescription>
            </VisuallyHidden>
        </DialogHeader>
        <div class="flex flex-col gap-y-4 pt-2">
            <SearchInput v-model="filter" :placeholder="$t('views.monitoring.teamComparison.dialogs.savedAnalyses.fields.search.placeholder')" class="w-auto min-w-64 max-w-full" />
            <div v-if="isLoading" class="p-8 flex justify-center items-center">
                <Spinner class="h-10 w-10" />
            </div>
            <Card v-else class="saved-analyses-table overflow-hidden">
                <DataTable
                    v-model:sorting="sorting"
                    v-model:filter="filter"
                    :data="items as RowItem[]"
                    :columns="columns"
                    :page="page"
                    :page-size="pageSize"
                    :page-size-options="pageSizeOptions"
                    :enable-pagination="false"
                >
                    <template #item[_selection]="{ item }">
                        <Checkbox :checked="selectedSetup === item.id" variant="circle" class="my-1" @update:checked="handleSelectionChange(item.id)" />
                    </template>
                    <template #item[created_at_utc]="{ item }">
                        {{ $d(parseISO(item.created_at_utc), 'short') }}
                    </template>
                    <template #item[_actions]="{ item }">
                        <Button variant="ghost" size="icon" class="text-red-500 hover:text-red-600 hover:bg-red-100 -mr-1" @click="handleDelete(item)">
                            <Trash2 class="w-4 h-4" />
                        </Button>
                    </template>
                </DataTable>
            </Card>
        </div>
        <DeleteSavedAnalysisDialog v-if="studyPeriodSetupToDelete" v-model:open="isDeleteDialogOpen" :study-period-setup="studyPeriodSetupToDelete" />
        <DialogFooter class="flex sm:justify-center pt-2">
            <DialogClose as-child>
                <CustomButton color-preset="white" class="w-auto min-w-44">{{ $t('views.monitoring.teamComparison.dialogs.savedAnalyses.buttons.cancel') }}</CustomButton>
            </DialogClose>
            <CustomButton class="w-auto min-w-44" :disabled="selectedSetup === null" @click="handleGenerate">{{ $t('views.monitoring.teamComparison.dialogs.savedAnalyses.buttons.generate') }}</CustomButton>
        </DialogFooter>
    </Dialog>
</template>
